import { defineStore } from 'pinia';
import type { RouteLocationNormalizedLoaded } from 'vue-router';
// @ts-ignore
import { callWithNuxt, useNuxtApp } from 'nuxt/app';
import { _getPageEnvironment, _initCatalog } from '@/api/pageData';
import type {
  CatalogInApp,
  RegionInApp,
  RegionInAppChange,
} from '~/interfaces/app';
import type {
  AutoFaqBlock,
  BreadcrumbsItem,
  DynamicPageContext,
  IPageEnvironment,
} from '~/interfaces/api';
import { cookieNames, defaultCatalog, maxAge } from '~/common/const';
import { useAnalyticsStore } from '~/stores/analytics';
import { logger } from '~/api/loggerLogic';
import initModelNew from '~/components/distributor/initModelNew';
import { initListingsData } from '~/components/distributor/initListingsData';
import { API } from '~/api/api';
import { getCategories } from '~/api/catalogCategory';
import initShopPromocodes from '~/components/distributor/initShopPromocodes';
import { getArticlesByCategory } from '~/api/global';
import { objToBase64 } from '~/utils/base64';

const pagesWithoutSeoData: string[] = [
  'about',
  'checkout',
  'checkout/approve',
  'checkout/thank-you',
  'favorite',
  'compare',
  'search',
  'error',
  'contacts',
  'press',
  'confirmation',
  'user-agreement',
  'cabinet',
  'cabinet-orders',
  'cabinet-addresses',
  'cabinet-profile',
  'cabinet-notification',
  'cabinet-help',
  'cabinet-help-slug',
  'purchase-terms',
  'mobile-purchase-terms',
  'legal',
  'account-deleted',
  'news',
  'promo-slug',
  'price',
  'price-clear',
  'uikit',
  'email-unsubscribe',
  'recommendation-system',
  'oplata-i-dostavka',
  'obmen-vozvrat-garantiya',
  'kak-oformit-zakaz',
];

export const defaultRegion: RegionInApp = {
  id: 1,
  subdomain: '',
  name: 'Москва',
  parents: [],
};

export const useGlobalStore = defineStore('global', () => {
  const nuxt = useNuxtApp();
  const cookieAdult = useCookie('adult');
  const headers = useRequestHeaders();
  const routeData = useRoute();
  const analyticsStore = useAnalyticsStore();
  // STATES
  const isProd = ref(false);
  const isDev = ref(false);
  const count = ref(1);
  const seoData = ref(null);
  const catalog = ref<CatalogInApp[]>(defaultCatalog);
  const isInitCatalog = ref(false);
  const isInitSearch = ref(false);
  const isMenuOpen = ref(false);
  const isCookieConfirm = ref(false);
  const region = ref<RegionInApp>(defaultRegion);
  const localRegion = ref<RegionInApp | undefined>(undefined);
  const device = ref(0);
  const webview = ref(false);
  const emptyLayout = ref(false);
  const sem = ref(0);
  const isBot = ref(false);
  const debugPanel = ref(false);
  const debugPanelAll = ref<string | undefined | boolean>(undefined);
  const debugPanelIds = ref(true);
  const backUrl = ref('');
  const time = ref(0);
  const requestSumTime = ref(0);
  const typePageFrom = ref('');
  const isLogged = ref(false);
  const isYandexBanner = ref(false);
  const isOpenAdultModal = ref(false);
  const isCaptcha = ref(false);
  const pageDistributorData = ref();
  const adult = ref<string | boolean>(false);
  const {
    isCrawler,
    isAndroid,
    isChrome,
    isMobile,
    isMobileOrTablet,
    userAgent,
  } = useDevice();
  const ScrollToQuestion = ref();
  const cancelTokenAll = ref<AbortController[]>([]);
  const perPageListingValue = ref<number>(48);
  const iconsArr = ref<{ [key: string]: () => void } | null>(null);
  const runtimeConfig = useRuntimeConfig();
  const appConfig = useAppConfig();
  isProd.value = runtimeConfig?.public?.isProd;
  isDev.value = appConfig?.isGlobalDev;
  // GETTERS

  // ACTIONS
  function setPerPageListingValue(per_page: number | null) {
    perPageListingValue.value = per_page || 48;
  }

  function setCancelToken(cancelToken: AbortController) {
    cancelTokenAll.value = [...cancelTokenAll.value, cancelToken];
  }

  function clearCancelToken() {
    cancelTokenAll.value = [];
  }

  function performCancelToken() {
    cancelTokenAll.value.forEach((token) => {
      token.abort();
    });
  }

  function setSeoData(newSeoData: {
    page?: {
      type?: string;
      component?: string;
      context?: string;
      blocks?: AutoFaqBlock[];
    };
    region: RegionInAppChange;
  }) {
    if (Object.keys(newSeoData).length === 0) {
      return;
    }
    seoData.value = newSeoData;
  }

  function setScrollToQuestion(el: HTMLElement) {
    ScrollToQuestion.value = el;
  }

  function setMenuOpen(v: boolean) {
    isMenuOpen.value = v;
  }

  async function initCatalog() {
    if (!isInitCatalog.value) {
      try {
        catalog.value = await _initCatalog();
      } catch {
      } finally {
        isInitCatalog.value = true;
      }
    }
  }

  function setSem(value: number) {
    sem.value = value;
  }

  function setIsCaptcha(value: boolean) {
    isCaptcha.value = value;
  }

  async function getDistributorData(
    envParams: {
      [key: string]: string;
    },
    route: RouteLocationNormalizedLoaded
  ): Promise<{
    resEnv: { [key: string]: string };
    distData: Record<string, string>;
  }> {
    const pageEnvironment: IPageEnvironment = await (_getPageEnvironment(
      envParams
    ) as Promise<IPageEnvironment>);
    let distData = {};
    if (pageEnvironment) {
      // const getSavePageLookup = (data) => {
      //   const result: { [key: string] } = {};
      //   if (data && typeof data === 'object') {
      //     result.region = data?.region;
      //     if (data?.page) {
      //       result.page = JSON.stringify({
      //         ...data?.page,
      //         blocks: data?.page?.blocks
      //           ? data?.page?.blocks?.length
      //           : data?.page?.blocks,
      //         breadcrumbs: data?.page?.breadcrumbs
      //           ? data?.page?.breadcrumbs?.length
      //           : data?.page?.breadcrumbs,
      //       });
      //     }
      //   }
      //   return result;
      // };
      const getComponent = (type: string) => {
        if (!type) return '';
        const modelsType = ['model-reviews', 'model-specs', 'model-questions'];
        if (modelsType.includes(type)) return 'model';
        return type;
      };
      const component = getComponent(pageEnvironment?.data?.page?.type);
      if (pageEnvironment?.data?.page?.type)
        pageEnvironment.data.page.component = component;
      const contextC: DynamicPageContext | string = pageEnvironment?.data?.page
        ?.context as DynamicPageContext;
      const blocks = pageEnvironment?.data?.page?.blocks;
      if (
        !pageEnvironment.data.region &&
        (!region.value.id || !region.value?.name)
      ) {
        setRegion({
          id: 1,
          subdomain: '',
          name: 'Москва',
          needQuestion: false,
          needRedirect: true,
        });
      } else if (
        pageEnvironment.data.region &&
        (!region.value.id || !region.value?.name)
      ) {
        setRegion(pageEnvironment.data.region);
      }
      setSeoData(pageEnvironment?.data);
      const store = {
        region: region.value,
        isBot: isBot.value,
        sem: sem.value,
        device: device.value,
        debugPanelAll: debugPanelAll.value,
        adult: adult.value,
        split: analyticsStore.additional?.split,
        analyticsData: analyticsStore,
        updateSeoTexts,
      };
      const getInitData = async () => {
        switch (component) {
          case 'model':
            return await initModelNew(
              contextC as DynamicPageContext,
              route,
              { ...store, analytics: analyticsStore },
              blocks
            );
          case 'brand': // страница товаров бренда
            return await initListingsData(
              1,
              contextC as DynamicPageContext,
              route.query,
              false,
              false,
              store
            );
          case 'category-list': // страница списка категорий
          case 'category-brand': // страница категорий бренда
          case 'category-preset': // страница с предустановками, например: "китайские телефоны"
          case 'category-discount': {
            // страница со скидками
            const data = await initListingsData(
              3,
              contextC as DynamicPageContext,
              route.query,
              component === 'category-discount',
              false,
              store
            ).catch(() => {});
            if (data)
              // @ts-ignore
              data.brandPresets =
                blocks && blocks.find((item) => item.label === 'brands')?.items;
            return data;
          }

          case 'promocodes-category': {
            // промокоды в категории
            const promocodesCategory = await API.get(`/coupons`, {
              params: {
                status: 'active',
                category_id: contextC?.category_id,
                page: 1,
                per_page: 10,
              },
            }).catch((e) => console.error('get coupons error', e));
            if (promocodesCategory) {
              return promocodesCategory.data?.list || [];
            } else {
              return [];
            }
          }
          case 'catalog-category': {
            // каталог категорий
            const getCat = await getCategories(contextC?.category_id, {
              params: {
                region_id: store.region.id,
                device: store.device || 1,
                tree: true,
                ...(adult.value ? { adult: adult.value } : {}),
              },
            });
            if (!!adult.value && getCat?.adult && !isBot.value) {
              setOpenAdultModal(true);
            }
            return getCat;
          }
          case 'shop-offers': // предложение магазина
            return await initListingsData(
              2,
              contextC as DynamicPageContext,
              route.query,
              false,
              false,
              store
            );
          case 'shop-promocodes': // промокоды магазина
            return await initShopPromocodes(contextC);
        }
      };

      distData = await getInitData();
      if (
        isBot.value &&
        distData &&
        typeof distData === 'object' &&
        'selectedCategory' in distData &&
        distData?.selectedCategory?.id
      ) {
        distData.articles = await getArticlesByCategory(
          distData.selectedCategory.id
        );
      }
    }
    return { resEnv: pageEnvironment as {}, distData };
  }

  async function getPageEnvironment(params) {
    const { route, isDesktop, isRouting, url } = params;
    try {
      setTime(+new Date());
      requestSumTime.value = 0;
      isLogged.value = true;
      if (cancelTokenAll.value.length > 0) {
        performCancelToken();
        clearCancelToken();
      }
      const pageRoute = route?.path.slice(1);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const [_, port = ''] = url.split(':');
      const oldSlug = seoData.value?.slug;
      const { isSubdomain, baseDomain, domain } = hostData(url, port);
      const cookieSession = useCookie('session');
      const firstEntry = useCookie('first-entry', {
        path: '/',
        domain,
        maxAge,
      });
      const session = cookieSession.value
        ? typeof cookieSession.value === 'string'
          ? JSON.parse(cookieSession.value)
          : cookieSession.value
        : null;
      if (!firstEntry.value) {
        let current = new Date();
        if (
          session &&
          session.ts &&
          session.ts.length > 21 &&
          !firstEntry.value
        ) {
          current = new Date(session.ts);
        }
        firstEntry.value = `${current.getDate()}.${
          current.getMonth() + 1
        }.${current.getFullYear()}`;
      }
      if (!isRouting) {
        initRegion();
      }

      initEnvironment(isDesktop);
      const noSlugOnServer = pagesWithoutSeoData.find((pageName) =>
        route?.name ? route.name.search(pageName) !== -1 : false
      )
        ? 'index'
        : '';
      if (noSlugOnServer && region.value?.id && region.value?.name) {
        if (seoData.value?.page?.type) {
          setSeoData({
            ...seoData.value,
            page: null,
          });
        }
        return;
      }

      const envParams = {
        slug: noSlugOnServer || pageRoute || 'index',
        region_id: `${region.value?.id}`,
        subdomain: region.value?.subdomain || '',
      };
      // @ts-ignore
      const [DistributorData] = await Promise.all([
        getDistributorData(envParams, route),
        initCatalog(),
      ]);
      const { resEnv } = DistributorData;
      setMenuOpen(false);
      if (resEnv?.cancelToken) {
        return;
      }
      if (!resEnv) {
        if (!import.meta.env.SSR && window) {
          window.history.replaceState(
            {},
            '',
            window.location.origin + '/' + envParams.slug
          );
        }
        throw showError({ statusCode: 500 });
      }
      pageDistributorData.value = objToBase64({
        data: DistributorData.distData,
      });
      const { data } = resEnv;
      if (data !== null && typeof data === 'object' && 'redirect' in data) {
        if (!import.meta.env.SSR) {
          const router = useRouter();
          if (router)
            router.push({
              path: (data as { redirect?: string }).redirect,
              query: {},
            });
        } else {
          callWithNuxt(nuxt, () =>
            navigateTo((data as { redirect?: string }).redirect, {
              redirectCode: 301,
            })
          );
        }
        return;
      }

      interface Data {
        page?: {
          breadcrumbs: BreadcrumbsItem[];
          component?: string;
        };
        region?: {
          id?: number;
        };
      }

      const dataAsData = data as Data;
      if (dataAsData?.page?.breadcrumbs) {
        dataAsData.page.breadcrumbs = dataAsData.page.breadcrumbs.map(
          (c: BreadcrumbsItem) =>
            Object.freeze(
              c.nav
                ? {
                    ...c,
                    nav: [
                      { title: c.title, slug: c.slug, current: true },
                      ...c.nav,
                    ],
                  }
                : c
            )
        );

        dataAsData?.page?.breadcrumbs.unshift(
          Object.freeze({
            slug: '',
            title: 'Главная',
          })
        );
      }
      // отображаем страницы акций на сайте
      if (!dataAsData.page && !route?.name.includes('promo')) {
        isSubdomain &&
          !dataAsData.region?.id &&
          callWithNuxt(nuxt, () =>
            navigateTo(`https://${baseDomain}`, {
              replace: true,
              redirectCode: 301,
            })
          );
        throw showError({ statusCode: 404 });
      }
      const slug =
        dataAsData?.page?.component === 'model'
          ? pageRoute.replace(/\/(otzyvy|voprosy)\/?$/, '/')
          : pageRoute;
      return {
        ...data,
        slug,
        oldSlug,
      };
    } catch (err) {
      // Отправить в логи fatal сообщение, если catch не по причине 404
      if (parseInt(err?.statusCode) !== 404) {
        const dataError = {
          catchError: typeof err === 'string' ? err : JSON.stringify(err),
          url,
          isClient: !import.meta.env.SSR,
          catchOutput: 'getPageEnvironment',
        };
        logger.fatal(dataError);
      }
      throw showError({
        statusCode: parseInt(err?.statusCode) === 404 ? 404 : 503,
      });
    }
  }

  function toggleDevice(val: boolean) {
    device.value = val ? 1 : 2;
  }

  function initEnvironment(isDesktop = true) {
    const cookieSem = useCookie('sem');
    const cookieDebugPanel = useCookie<string | undefined | boolean>(
      cookieNames.debugPanel,
      {
        default: () => undefined,
      }
    );
    const cookieDebugPanelAll = useCookie<string | undefined | boolean>(
      cookieNames.debugAll,
      {
        default: () => undefined,
      }
    );
    const cookieConfirm = useCookie<string | undefined | boolean>(
      'cookie-confirm',
      {
        default: () => undefined,
      }
    );
    if (routeData.query?.utm_medium === 'cpc' || cookieSem.value) {
      setSem(1);
    }
    if (routeData.query._cp === '0') {
      cookieDebugPanel.value = undefined;
    } else if (
      headers['x-internal-network'] === '1' &&
      (routeData.query._cp === '1' || cookieDebugPanel.value)
    ) {
      setDebugPanel(true);
    } else {
      setDebugPanel(false);
    }

    if (cookieDebugPanelAll.value) {
      setDebugPanelAll('1');
    }
    if (cookieConfirm.value) {
      setIsCookieConfirm(true);
    }
    // console.log('x-internal-network', headers['x-internal-network']);
    // ставим cookies для сайта и аналитики
    if (cookieAdult.value || isCrawler) setAdult('1');

    setBot(
      isCrawler ||
        (isAndroid &&
          isChrome &&
          isMobile &&
          isMobileOrTablet &&
          (userAgent.includes('power') || userAgent.includes('(2022)'))) ||
        userAgent.includes('YandexRenderResourcesBot/1.0;') ||
        userAgent.includes('YandexBot/3.0') ||
        userAgent.includes('YandexUserproxy') ||
        userAgent.includes('YandexAdditional')
    );

    toggleDevice(isDesktop);
    analyticsStore.initAnalytics();
  }

  function updateSeoTexts(data) {
    const updateSeoData = (text: string): string => {
      if (text) {
        const replaceStr = (_match: string, p1: string): string => {
          return (data[p1] || 0).toString();
        };
        return text.replace(
          /{{(shop_count|price_min|price_max|offer_count)}}/gi,
          replaceStr
        );
      }
      return '';
    };

    const newTexts = ['title', 'description', 'text'].reduce(
      (sum, key) => ({
        ...sum,
        [key]: updateSeoData(
          seoData.value?.page && key in seoData.value?.page
            ? seoData.value.page[key]
            : ''
        ),
      }),
      {}
    );

    if (seoData.value?.page) {
      seoData.value.page = {
        ...seoData.value.page,
        ...newTexts,
      };
    }

    return newTexts;
  }

  function setBot(val: boolean) {
    isBot.value = val;
  }

  function setDebugPanel(value: boolean) {
    debugPanel.value = value;
  }

  function setIsCookieConfirm(value: boolean) {
    isCookieConfirm.value = value;
  }

  function setDebugPanelAll(val: boolean | string) {
    debugPanelAll.value = val ? '1' : undefined;
  }

  function setDebugPanelIds(val: boolean) {
    debugPanelIds.value = val;
  }

  function setRegion(val: RegionInAppChange) {
    region.value = { ...region.value, ...val };
  }

  function setBackUrl(val: string) {
    backUrl.value = val;
  }

  function setOpenAdultModal(val: boolean) {
    isOpenAdultModal.value = val;
  }

  function setAdult(val: string) {
    adult.value = val;
  }

  function initRegion() {
    const [url, port = ''] = headers.host.split(':');
    const { domains, isSubdomain, domain } = hostData(url, port);
    const cookieRegion = useCookie('region', {
      path: '/',
      domain,
      maxAge,
    });
    const regionCookie = cookieRegion.value
      ? typeof cookieRegion.value === 'string'
        ? JSON.parse(cookieRegion.value)
        : cookieRegion.value
      : null;

    const regionId = regionCookie?.id || 0;
    const regionName = regionCookie?.name || '';
    const regionSubdomain = regionCookie?.subdomain || '';
    if (!isSubdomain) {
      // Если зашел в корневой домен
      if (!regionId || !regionName) {
        // Зашел первый раз в корневой домен. Нет куки или какого-то из параметров - выдача по IP
        setRegion({
          ...defaultRegion,
          needQuestion: true,
        });
      } else if (!!regionId && !!regionSubdomain && !!regionName) {
        // Ситуация когда есть кука с поддомена, но мы находимся в корне
        setRegion({
          ...defaultRegion,
          needQuestion: true,
        });
      } else if (regionId === 1) {
        // Ситуация когда есть кука и это Москва
        setRegion({
          ...defaultRegion,
          needQuestion: false,
        });
      } else if (!!regionId && regionId !== 1) {
        setRegion({
          ...regionCookie,
          needQuestion: false,
        });
      }
    } else {
      // Если зашел в субдомен
      const subdomain = domains[0];
      if (regionId && regionSubdomain === subdomain) {
        // Ситауация когда есть кука совпадает с субдоменом -  выдача по куке
        setRegion({
          id: regionId,
          subdomain: regionSubdomain,
          name: regionName,
          needQuestion: false,
        });
      } else {
        // не совпадает поддомен / нет куки запрос по поддомену(page-info происходит запрос региона)
        setRegion({
          id: 0,
          subdomain,
          name: '',
          needQuestion: true,
        });
      }
    }
    return !!regionCookie?.id && !!regionCookie?.name;
  }

  function setEmptyLayout(value: boolean) {
    emptyLayout.value = value;
  }

  function setWebview(value: boolean) {
    webview.value = value;
  }

  function setTime(value: number) {
    time.value = value;
  }

  function setTypePageFrom(value: string) {
    typePageFrom.value = value;
  }

  function addRequestSumTime(time: number) {
    if (isLogged.value) {
      requestSumTime.value = requestSumTime.value + time;
    }
  }

  function setIsLogged(value: boolean) {
    isLogged.value = value;
  }

  function setLocalRegion(value: RegionInApp) {
    localRegion.value = value;
  }

  function setYandexBanner(value: boolean) {
    isYandexBanner.value = value;
  }

  function setIconsArr(value: { [key: string]: () => void }) {
    iconsArr.value = value;
  }

  return {
    setAdult,
    setOpenAdultModal,
    isOpenAdultModal,
    setRegion,
    count,
    getPageEnvironment,
    seoData,
    updateSeoTexts,
    catalog,
    isInitCatalog,
    initCatalog,
    isMenuOpen,
    setMenuOpen,
    webview,
    sem,
    setSem,
    region,
    device,
    isBot,
    adult,
    debugPanel,
    debugPanelAll,
    debugPanelIds,
    setDebugPanel,
    setDebugPanelIds,
    setDebugPanelAll,
    backUrl,
    setBackUrl,
    emptyLayout,
    setEmptyLayout,
    setWebview,
    isInitSearch,
    isCookieConfirm,
    setIsCookieConfirm,
    setScrollToQuestion,
    ScrollToQuestion,
    time,
    setTypePageFrom,
    typePageFrom,
    addRequestSumTime,
    requestSumTime,
    setIsLogged,
    setIsCaptcha,
    isCaptcha,
    localRegion,
    setLocalRegion,
    cancelTokenAll,
    setCancelToken,
    clearCancelToken,
    pageDistributorData,
    perPageListingValue,
    setPerPageListingValue,
    isYandexBanner,
    setYandexBanner,
    setIconsArr,
    iconsArr,
    isProd,
  };
});
